import React, { useState, useEffect } from "react";
import { apiService } from "../../utils/api";
import { FaSearch } from "react-icons/fa";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Button, Box, TextField, InputAdornment, Typography, Grid, Card, CardContent, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import "./AttendanceManagement.css";

function AttendanceManagement() {
  const [pendingAttendances, setPendingAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchPendingAttendances();
    fetchAttendanceStats();
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await apiService.getBatches();
      if (response.data.success) {
        setBatches(response.data.batches);
      }
    } catch (err) {
      console.error("Error fetching batches:", err);
    }
  };

  const fetchPendingAttendances = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPendingAttendances();
      if (response.data.success) {
        setPendingAttendances(response.data.pendingAttendances);
      }
    } catch (err) {
      console.error("Error fetching pending attendances:", err);
      setError("Failed to load pending attendances");
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceStats = async () => {
    try {
      const response = await apiService.getAttendanceStats(dateRange.startDate, dateRange.endDate);
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error("Error fetching attendance stats:", err);
    }
  };

  const handleStatusChange = async (attendanceId, status, notes = "") => {
    try {
      const response = await apiService.updateAttendanceStatus(attendanceId, status, notes);
      if (response.data.success) {
        // Update the local state to reflect the change
        setPendingAttendances(pendingAttendances.filter((a) => a.id !== attendanceId));
        fetchAttendanceStats(); // Refresh stats
      }
    } catch (err) {
      console.error("Error updating attendance status:", err);
      setError("Failed to update attendance status");
    }
  };

  const handleDateRangeChange = (e) => {
    setDateRange({
      ...dateRange,
      [e.target.name]: e.target.value,
    });
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatTime = (timeString) => {
    return timeString ? new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "N/A";
  };

  // Filter attendances based on search term and selected batch
  const filteredAttendances = pendingAttendances.filter((attendance) => {
    const matchesSearch = attendance.student.username.toLowerCase().includes(searchTerm.toLowerCase()) || formatDate(attendance.date).toLowerCase().includes(searchTerm.toLowerCase());

    const matchesBatch = !selectedBatch || attendance.student.enrolledBatches[0].id === selectedBatch;

    console.log(selectedBatch, "=====================================");
    console.log(attendance.student.enrolledBatches[0].id, "=====//////////////////////////////");

    return matchesSearch && matchesBatch;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Attendance Management
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Attendance Statistics
          </Typography>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="date" label="Start Date" name="startDate" value={dateRange.startDate} onChange={handleDateRangeChange} inputProps={{ max: dateRange.endDate }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                name="endDate"
                value={dateRange.endDate}
                onChange={handleDateRangeChange}
                inputProps={{
                  min: dateRange.startDate,
                  max: new Date().toISOString().split("T")[0],
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="contained" onClick={fetchAttendanceStats} fullWidth>
                Apply
              </Button>
            </Grid>
          </Grid>

          {stats && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4" align="center">
                      {stats.total}
                    </Typography>
                    <Typography variant="subtitle1" align="center">
                      Total
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: "success.light" }}>
                  <CardContent>
                    <Typography variant="h4" align="center">
                      {stats.approved}
                    </Typography>
                    <Typography variant="subtitle1" align="center">
                      Approved
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: "warning.light" }}>
                  <CardContent>
                    <Typography variant="h4" align="center">
                      {stats.pending}
                    </Typography>
                    <Typography variant="subtitle1" align="center">
                      Pending
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: "error.light" }}>
                  <CardContent>
                    <Typography variant="h4" align="center">
                      {stats.rejected}
                    </Typography>
                    <Typography variant="subtitle1" align="center">
                      Rejected
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Pending Attendances</Typography>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Batch</InputLabel>
              <Select value={selectedBatch} label="Filter by Batch" onChange={(e) => setSelectedBatch(e.target.value)}>
                <MenuItem value="">All Batches</MenuItem>
                {batches.map((batch) => (
                  <MenuItem key={batch.id} value={batch.id}>
                    {batch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                placeholder="Search attendances..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <Typography>Loading pending attendances...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: "center", p: 3 }}>
              <Typography color="error" gutterBottom>
                {error}
              </Typography>
              <Button variant="contained" onClick={fetchPendingAttendances}>
                Try Again
              </Button>
            </Box>
          ) : filteredAttendances.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 3 }}>
              <Typography>No pending attendance records found.</Typography>
              {searchTerm && <Typography color="text.secondary">Try adjusting your search criteria.</Typography>}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="attendance table">
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Check-in</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAttendances.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((attendance) => (
                    <TableRow key={attendance.id}>
                      <TableCell>{attendance.student.username}</TableCell>
                      <TableCell>{attendance.student.enrolledBatches[0].name}</TableCell>
                      <TableCell>{formatDate(attendance.date)}</TableCell>
                      <TableCell>{formatTime(attendance.checkInTime)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button variant="contained" color="success" size="small" onClick={() => handleStatusChange(attendance.id, "approved")}>
                            Approve
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => {
                              const notes = prompt("Reason for rejection:");
                              if (notes !== null) {
                                handleStatusChange(attendance.id, "rejected", notes);
                              }
                            }}
                          >
                            Reject
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={filteredAttendances.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default AttendanceManagement;
